import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAlert } from 'react-alert';

import { useActions } from '../../../../../hooks/useActions';
import { useModalDetails } from '../../../../../hooks/useModalDetails';
import { useProcessing } from '../../../../../hooks/useProcessing';
import { useCancellationToken } from '../../../../../hooks/useTokenCancellation';
import { updateModalState } from '../../../../../store/redux-slices/modals';
import { personMembershipIsActiveByMembershipIdSelector } from '../../../../../store/redux-slices/people/selectors';
import { DELETE_MEMBERSHIP_PROCESSING } from '../../../../../store/redux-slices/processes/constants';
import { DELETE_KE_MEMBERSHIP_MODAL_NAME } from './deleteKEMembershipModal.const';
import { deleteKEMembership } from '../../../../../store/saga-slices/membership/kidnapAndExtortion';
import { IDeleteKEMembershipPayload } from '../../../../../store/saga-slices/membership/kidnapAndExtortion/models';
import { DeleteKEMembershipModalDetails } from './deleteKEMembershipModal.models';

type UseDeleteKEMembershipModalProps = {
  id?: string;
};

export const useDeleteKEMembershipModal = ({
  id,
}: UseDeleteKEMembershipModalProps) => {
  const { t } = useTranslation();
  const alert = useAlert();
  const cancellationToken = useCancellationToken();
  const isDeleting = useProcessing(DELETE_MEMBERSHIP_PROCESSING);
  const UpdateModalState = useActions(updateModalState);
  const DeleteMembership = useActions(deleteKEMembership);

  const {
    membershipId,
    productType,
    personName,
  }: DeleteKEMembershipModalDetails = useModalDetails(
    DELETE_KE_MEMBERSHIP_MODAL_NAME + id,
  );

  const personMembershipIsActive = useSelector(
    personMembershipIsActiveByMembershipIdSelector(membershipId),
  );

  const onBackHandler = () => {
    UpdateModalState(DELETE_KE_MEMBERSHIP_MODAL_NAME + id);
  };

  const deleteMembershipHandler = useCallback(() => {
    const payload: IDeleteKEMembershipPayload = {
      membershipId,
      cancellationToken,
      isActive: personMembershipIsActive,
      success: () => {
        alert.success(t('alerts.membership-delete-success'));
        UpdateModalState(DELETE_KE_MEMBERSHIP_MODAL_NAME + id);
      },
      error: () => {
        alert.error(t('alerts.membership-delete-error'));
      },
    };
    DeleteMembership(payload);
  }, [
    membershipId,
    cancellationToken,
    personMembershipIsActive,
    DeleteMembership,
    alert,
    t,
    UpdateModalState,
    id,
  ]);

  return {
    onBackHandler,
    productType,
    personName,
    deleteMembershipHandler,
    isDeleting,
    personMembershipIsActive,
  };
};
